import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, Crown, CheckCircle, BookOpen, GraduationCap, MessageCircle, X, Calendar, Play, Clock, Users, Award } from 'lucide-react';
import SEO from '../components/shared/SEO';

// Service Pillar Card
const ServicePillar = ({ icon: Icon, title, subtitle, description, features, cta, ctaLink, color, secondaryCta, secondaryCtaLink }) => (
  <div className="bg-[#121212] border border-white/10 p-8 flex flex-col h-full hover:border-white/20 transition-colors">
    {/* Icon & Title */}
    <div className="flex items-center gap-4 mb-6">
      <div 
        className="w-14 h-14 flex items-center justify-center"
        style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <div>
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color }}>
          {subtitle}
        </span>
        <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase">
          {title}
        </h3>
      </div>
    </div>
    
    {/* Description */}
    <p className="text-white/60 leading-relaxed mb-6 flex-1">
      {description}
    </p>
    
    {/* Features */}
    <ul className="space-y-2 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color }} />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
    
    {/* CTAs */}
    <div className="space-y-3 mt-auto">
      <Link
        to={ctaLink}
        className="w-full py-3 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
        style={{ backgroundColor: color, color: '#000' }}
      >
        {cta}
        <ArrowRight className="w-4 h-4" />
      </Link>
      {secondaryCta && (
        <Link
          to={secondaryCtaLink}
          className="w-full py-3 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 border border-white/20 text-white hover:bg-white/5"
        >
          {secondaryCta}
        </Link>
      )}
    </div>
  </div>
);

// Carrier Chronicles Preview Card
const ChroniclePreview = ({ title, category, slug }) => (
  <Link 
    to={`/carrier-chronicles/${slug}`}
    className="bg-[#121212] border border-white/10 p-6 hover:border-[#D4AF37]/50 transition-colors group"
  >
    <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest">{category}</span>
    <h4 className="text-white font-bold mt-2 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
      {title}
    </h4>
  </Link>
);

// Academy Course Preview Card
const AcademyCourseCard = ({ icon: Icon, title, lessons, duration, description, color, popular }) => (
  <div className="bg-[#121212] border border-white/10 p-6 hover:border-[#D4AF37]/30 transition-all group relative">
    {popular && (
      <div className="absolute -top-3 right-4 px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold uppercase">
        Most Popular
      </div>
    )}
    <div className="flex items-start gap-4 mb-4">
      <div 
        className="w-12 h-12 flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <h4 className="text-white font-bold group-hover:text-[#D4AF37] transition-colors">
          {title}
        </h4>
        <div className="flex items-center gap-3 text-white/40 text-xs mt-1">
          <span className="flex items-center gap-1">
            <Play className="w-3 h-3" />
            {lessons} lessons
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {duration}
          </span>
        </div>
      </div>
    </div>
    <p className="text-white/50 text-sm leading-relaxed">
      {description}
    </p>
  </div>
);

// Floating Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Panel */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-80 bg-[#0D0D0D] border border-white/10 shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="bg-[#D4AF37] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black/20 rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="text-black font-bold text-sm">Queen B Logistics</p>
                <p className="text-black/70 text-xs">We typically reply instantly</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-black/70 hover:text-black"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-4">
            <p className="text-white/80 text-sm">
              Hi there! 👋 How can we help you today?
            </p>
            
            {/* Quick Actions */}
            <div className="space-y-2">
              <a 
                href="https://calendly.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full p-3 bg-[#D4AF37] text-black font-medium text-sm flex items-center justify-center gap-2 hover:bg-[#C4A030] transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Schedule a Consultation
              </a>
              <Link 
                to="/intake"
                className="w-full p-3 border border-white/20 text-white text-sm flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                <Building2 className="w-4 h-4" />
                Start Your Application
              </Link>
              <Link 
                to="/academy"
                className="w-full p-3 border border-white/20 text-white text-sm flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                <GraduationCap className="w-4 h-4" />
                Browse Academy Courses
              </Link>
            </div>
            
            {/* Contact Info */}
            <div className="pt-3 border-t border-white/10 text-center">
              <p className="text-white/40 text-xs">
                Or email us at <a href="mailto:dispatch@queenblogistics.com" className="text-[#D4AF37] hover:underline">dispatch@queenblogistics.com</a>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all transform hover:scale-105 ${
          isOpen 
            ? 'bg-white/10 text-white' 
            : 'bg-[#D4AF37] text-black'
        }`}
        data-testid="chat-widget-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </button>
      
      {/* Notification Badge */}
      {!isOpen && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
          1
        </span>
      )}
    </div>
  );
};

const HomePage = () => {
  const servicePillars = [
    {
      icon: Building2,
      title: 'Build',
      subtitle: 'New Authority',
      description: 'Launch your trucking business with a structured, phase-tracked system. From LLC formation through authority activation and launch readiness.',
      features: [
        'Structured 5-phase launch system',
        'MC authority filing guidance',
        'Compliance setup preparation',
        'Launch dispatch support included',
      ],
      cta: 'Start Building',
      ctaLink: '/build',
      secondaryCta: 'View Pricing',
      secondaryCtaLink: '/build#pricing',
      color: '#CD7F32',
    },
    {
      icon: Shield,
      title: 'Protect',
      subtitle: 'Compliance',
      description: 'Structured compliance oversight for active operators. Insurance monitoring, IFTA tracking, UCR filings, and regulatory risk management.',
      features: [
        'Insurance expiration monitoring',
        'IFTA & IRP deadline tracking',
        'UCR filing reminders',
        'Safety status visibility',
      ],
      cta: 'Get Compliance Monitoring',
      ctaLink: '/protect',
      secondaryCta: 'View Plans',
      secondaryCtaLink: '/protect#plans',
      color: '#C0C0C0',
    },
    {
      icon: Crown,
      title: 'Grow',
      subtitle: 'Dispatch',
      description: 'Revenue-focused dispatch operations for active operators. RPM optimization, broker management, load planning, and performance tracking.',
      features: [
        'Dedicated dispatcher support',
        'Rate negotiation & RPM focus',
        'Load planning strategy',
        'Weekly performance reporting',
      ],
      cta: 'Start Dispatching',
      ctaLink: '/intake',
      secondaryCta: 'View Packages',
      secondaryCtaLink: '/grow#plans',
      color: '#D4AF37',
    },
  ];

  const chroniclesPreviews = [
    { title: 'What Every New Authority Must Do in the First 90 Days', category: 'Authority & Compliance', slug: 'first-90-days-new-authority' },
    { title: 'Why We Call It Premium Freight Operations', category: 'The Queen B Philosophy', slug: 'premium-freight-operations' },
    { title: 'How to Reduce Deadhead Without Killing Profit', category: 'Premium Strategy', slug: 'reduce-deadhead-miles' },
  ];

  const academyCourses = [
    {
      icon: Award,
      title: 'CDL Mastery Program',
      lessons: 24,
      duration: '8+ hours',
      description: 'Complete CDL preparation with real-world scenarios, practice exercises, and expert tips for passing your exam.',
      color: '#D4AF37',
      popular: true,
    },
    {
      icon: Users,
      title: 'Freight Dispatcher Certification',
      lessons: 18,
      duration: '6+ hours',
      description: 'Learn load negotiation, customer relations, and the systems that top dispatchers use to maximize revenue.',
      color: '#C0C0C0',
      popular: false,
    },
    {
      icon: Building2,
      title: 'Trucking Business Blueprint',
      lessons: 20,
      duration: '7+ hours',
      description: 'Build a profitable trucking company from scratch. From LLC setup to scaling operations.',
      color: '#CD7F32',
      popular: false,
    },
    {
      icon: Crown,
      title: 'Freight Broker Foundations',
      lessons: 16,
      duration: '5+ hours',
      description: 'Master the art of freight brokerage — licensing, operations, and building lasting shipper relationships.',
      color: '#8B5CF6',
      popular: false,
    },
  ];

  return (
    <div data-testid="home-page" className="bg-[#0A0A0A]">
      <SEO 
        title="Premium Freight Operations | Queen B Logistics"
        description="Queen B Logistics is a limited-access freight operations firm specializing in revenue optimization, compliance oversight, lane strategy, and operational infrastructure for owner-operators and small fleets."
        canonical="https://queenblogistics.com"
        keywords="freight operations, transportation management, revenue optimization, owner operator dispatch, trucking compliance, lane strategy, freight dispatch services"
      />
      
      {/* HERO SECTION */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bee-dispatch/artifacts/yjyel37i_IMG_9693.png)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/85 to-[#0A0A0A]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="max-w-3xl">
            {/* Limited Access Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
              <span className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">
                Limited Access • Quality Over Quantity
              </span>
            </div>
            
            {/* Main Headline */}
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase leading-none mb-4">
              Premium Freight<br /><span className="text-[#D4AF37]">Operations</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-[#D4AF37] font-medium text-xl md:text-2xl mb-6">
              Full-Service Transportation Management & Revenue Strategy
            </p>
            
            {/* Description */}
            <p className="text-white/70 text-lg max-w-2xl mb-10 leading-relaxed">
              Queen B Logistics is a limited-access freight operations firm specializing in revenue optimization, 
              compliance oversight, lane strategy, and operational infrastructure for serious owner-operators and small fleets.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/inside-the-hive"
                className="px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
                data-testid="hero-cta-primary"
              >
                Inside the Hive
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/intake"
                className="px-8 py-4 border border-white/30 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                Join the Hive
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section className="py-20 md:py-32 bg-[#050505]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
              Our Services
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              Build • Protect • Grow
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Three structured service pillars designed to support your business at every stage — 
              whether you're launching, protecting, or scaling your operation.
            </p>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {servicePillars.map((pillar) => (
              <ServicePillar key={pillar.title} {...pillar} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY QUEEN B */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
                The Queen B Difference
              </span>
              <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-6">
                Infrastructure,<br />Not Guesswork
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                We do more than book loads. We protect your authority, increase your profitability, 
                and position your transportation business for controlled, long-term growth.
              </p>
              
              <div className="space-y-4">
                {[
                  'Strategic dispatch management & rate negotiation',
                  'Compliance monitoring & regulatory oversight',
                  'Performance reporting & revenue tracking',
                  'Financial organization & operational infrastructure',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-[#121212] border border-white/10 p-8">
              <div className="text-center mb-8">
                <p className="text-[#D4AF37] font-bold text-lg mb-2">Quality Over Quantity</p>
                <p className="text-white/50 text-sm">By Application Only</p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between py-4 border-b border-white/10">
                  <span className="text-white/70">Client Acceptance Rate</span>
                  <span className="text-white font-bold">Limited</span>
                </div>
                <div className="flex items-center justify-between py-4 border-b border-white/10">
                  <span className="text-white/70">Service Model</span>
                  <span className="text-white font-bold">Premium</span>
                </div>
                <div className="flex items-center justify-between py-4 border-b border-white/10">
                  <span className="text-white/70">Commitment Level</span>
                  <span className="text-white font-bold">Serious Operators Only</span>
                </div>
              </div>
              
              <Link
                to="/intake"
                className="w-full mt-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
              >
                Apply Now
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="how-it-works-section">
        {/* Subtle hexagonal backdrop */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hive-hex-home" x="0" y="0" width="80" height="70" patternUnits="userSpaceOnUse">
                <path d="M40 0 L80 22 L80 48 L40 70 L0 48 L0 22 Z" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hive-hex-home)" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
              Your Path Into The Hive
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              How It Works
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              A deliberate, 4-step process — because the right operators deserve the right onboarding.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply', description: 'Submit your application. No phone number to call, no chasing — we review every operator individually.', icon: Users },
              { step: '02', title: 'Approved', description: 'Selected carriers receive an access code via email — your invitation into the Hive.', icon: Crown },
              { step: '03', title: 'Onboard', description: 'Sign your agreements, configure your dispatch settings, and meet your dedicated dispatcher.', icon: Shield },
              { step: '04', title: 'Scale', description: 'Loads start flowing. Compliance stays protected. Revenue grows with intention.', icon: ArrowRight },
            ].map((s, idx) => (
              <div key={idx} className="relative bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-['Barlow_Condensed'] text-4xl font-black text-[#D4AF37]/20">{s.step}</span>
                  <s.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-2">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:text-[#F5D47B] transition-colors uppercase tracking-wider text-sm"
            >
              Common Questions
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CARRIER CHRONICLES PREVIEW */}
      <section className="py-20 md:py-32 bg-[#050505]" data-testid="chronicles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
                  The Carrier Chronicles
                </span>
              </div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase">
                Industry Insights & Strategy
              </h2>
            </div>
            <Link 
              to="/carrier-chronicles"
              className="text-[#D4AF37] hover:text-[#F5D47B] font-medium flex items-center gap-2 transition-colors"
            >
              View All Articles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {chroniclesPreviews.map((article) => (
              <ChroniclePreview key={article.slug} {...article} />
            ))}
          </div>
        </div>
      </section>

      {/* ACADEMY PREVIEW */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="academy-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
                  Queen B Academy
                </span>
              </div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase">
                Master Your Craft
              </h2>
              <p className="text-white/50 mt-2 max-w-xl">
                Premium courses designed by industry professionals. Get certified and gain the skills to dominate the freight industry.
              </p>
            </div>
            <Link 
              to="/academy"
              className="text-[#D4AF37] hover:text-[#F5D47B] font-medium flex items-center gap-2 transition-colors"
              data-testid="academy-preview-link"
            >
              Browse All Courses
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {academyCourses.map((course) => (
              <AcademyCourseCard key={course.title} {...course} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#121212] border border-white/10">
              <span className="text-white/50 text-sm">Starting at</span>
              <span className="text-[#D4AF37] font-bold text-2xl">$250</span>
              <span className="text-white/50 text-sm">per course</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
            Ready to Run Consistent<br />and <span className="text-[#D4AF37]">Profitable?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Join the hive and experience premium freight operations that puts your success first.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/intake"
              className="w-full sm:w-auto px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2"
              data-testid="final-cta-join"
            >
              Join the Hive
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/hive/login"
              className="w-full sm:w-auto px-10 py-5 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors"
              data-testid="final-cta-login"
            >
              Access The Hive
            </Link>
          </div>
        </div>
      </section>

      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
};

export default HomePage;
